"use client";

import React from "react";
import { Area, AreaChart, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ArrowUpRight, ArrowDownRight, Activity, Wallet, TrendingUp, Clock, ChevronRight } from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const portfolioData = [{
  time: "09:00",
  value: 4200
}, {
  time: "10:00",
  value: 4150
}, {
  time: "11:00",
  value: 4080
}, {
  time: "12:00",
  value: 3900
}, {
  time: "13:00",
  value: 3850
}, {
  time: "14:00",
  value: 3700
}, {
  time: "15:00",
  value: 3650
}, {
  time: "16:00",
  value: 3580
}, {
  time: "17:00",
  value: 3450
}];
const trendingAssets = [{
  id: "fixed",
  symbol: "30YF",
  name: "30-Year Fixed",
  price: "6.85%",
  change: "-0.05%",
  isPositive: true,
  data: [{
    v: 6.95
  }, {
    v: 6.92
  }, {
    v: 6.90
  }, {
    v: 6.88
  }, {
    v: 6.85
  }]
}, {
  id: "arm",
  symbol: "5/1A",
  name: "5/1 ARM",
  price: "6.12%",
  change: "+0.02%",
  isPositive: false,
  data: [{
    v: 6.05
  }, {
    v: 6.08
  }, {
    v: 6.10
  }, {
    v: 6.11
  }, {
    v: 6.12
  }]
}, {
  id: "fha",
  symbol: "FHA",
  name: "FHA Loan",
  price: "6.25%",
  change: "-0.10%",
  isPositive: true,
  data: [{
    v: 6.45
  }, {
    v: 6.40
  }, {
    v: 6.35
  }, {
    v: 6.30
  }, {
    v: 6.25
  }]
}, {
  id: "va",
  symbol: "VA",
  name: "VA Loan",
  price: "6.15%",
  change: "-0.08%",
  isPositive: true,
  data: [{
    v: 6.30
  }, {
    v: 6.25
  }, {
    v: 6.22
  }, {
    v: 6.18
  }, {
    v: 6.15
  }]
}];
const recentGains = [{
  id: 1,
  asset: "Conventional",
  symbol: "CONV",
  profit: "Approved",
  time: "2 mins ago",
  type: "Purchase"
}, {
  id: 2,
  asset: "Refinance",
  symbol: "REFI",
  profit: "Pending",
  time: "15 mins ago",
  type: "Cash-Out"
}, {
  id: 3,
  asset: "First-Time",
  symbol: "FTB",
  profit: "Approved",
  time: "1 hour ago",
  type: "Purchase"
}, {
  id: 4,
  asset: "Jumbo Loan",
  symbol: "JMB",
  profit: "Review",
  time: "3 hours ago",
  type: "Purchase"
}];
const timeFrames = ["30Y", "20Y", "15Y", "10Y", "5Y"];
const chartConfig = {
  value: {
    label: "Monthly Payment",
    color: "var(--secondary)"
  }
};
export default function MortgageEstimatorTool() {
  const [activeTimeframe, setActiveTimeframe] = React.useState("30Y");
  return <section data-section-id="261492" id="mortgage-estimator-tool" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#43603712_1px,transparent_1px),linear-gradient(to_bottom,#43603712_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background"></div>
      </div>

      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-secondary/5 blur-[120px] rounded-lg pointer-events-none z-0"></div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-secondary/30 text-secondary bg-secondary/10">
            <Activity className="w-3 h-3 mr-1" /> Live Market Rates
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight mb-4 text-foreground">
            Mortgage Estimator Tool
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default">
            Calculate your potential monthly payments with our institutional-grade estimator. Precision tools designed for the modern homeowner.
          </p>
        </div>

        {}
        <div className="w-full lg:w-[90%] mx-auto bg-card/40 backdrop-blur-2xl border border-border rounded-md shadow-4xl overflow-hidden flex flex-col">
          
          {}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-md bg-secondary"></div>
              <div className="w-3 h-3 rounded-md bg-secondary/60"></div>
              <div className="w-3 h-3 rounded-md bg-secondary/30"></div>
            </div>
            <div className="text-sm font-mono text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" /> Rates Updated: Just Now
            </div>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            
            {}
            <div className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-border p-6 bg-primary/5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-6 flex items-center gap-2 font-default">
                <TrendingUp className="w-4 h-4 text-secondary" /> Current Rates
              </h3>
              <div className="space-y-6">
                {trendingAssets.map((asset, i) => <div key={asset.id} data-index={i} className="flex items-center justify-between group cursor-pointer">
                    <div>
                      <div className="font-semibold text-foreground group-hover:text-secondary transition-colors font-default">{asset.symbol}</div>
                      <div className="text-xs text-muted-foreground font-default">{asset.name}</div>
                    </div>
                    <div className="w-16 h-8 opacity-70 group-hover:opacity-100 transition-opacity">
                      <ChartContainer config={{
                    value: {
                      color: asset.isPositive ? "hsl(43 60% 55%)" : "hsl(222 47% 11%)"
                    }
                  }} className="w-full h-full">
                        <LineChart data={asset.data}>
                          <Line type="monotone" dataKey="v" stroke="var(--color-value)" strokeWidth={2} dot={false} isAnimationActive={false} />
                        </LineChart>
                      </ChartContainer>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-foreground">{asset.price}</div>
                      <div className={`text-xs flex items-center justify-end ${asset.isPositive ? 'text-secondary' : 'text-muted-foreground'}`}>
                        {asset.isPositive ? <ArrowDownRight className="w-3 h-3 mr-0.5" /> : <ArrowUpRight className="w-3 h-3 mr-0.5" />}
                        {asset.change}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {}
            <div className="lg:col-span-6 p-6 lg:p-8 relative">
              <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2 font-default">
                    <Wallet className="w-4 h-4 text-secondary" /> Estimated Monthly Payment
                  </h3>
                  <div className="text-4xl md:text-6xl font-bold font-serif tracking-tight text-primary">
                    $3,450.20
                  </div>
                  <div className="text-secondary flex items-center mt-2 font-medium font-default">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    Based on 6.85% APR
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {timeFrames.map((tf, i) => <Button key={tf} data-index={i} variant={activeTimeframe === tf ? "default" : "ghost"} size="sm" onClick={() => setActiveTimeframe(tf)} className={`text-xs px-3 rounded-md font-medium transition-colors ${activeTimeframe === tf ? 'bg-primary text-primary-foreground' : 'bg-secondary/5 text-muted-foreground hover:bg-secondary/10'}`}>
                      {tf}
                    </Button>)}
                </div>
              </div>

              <div className="h-[250px] md:h-[300px] w-full">
                <ChartContainer config={chartConfig} className="w-full h-full">
                  <AreaChart data={portfolioData} margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                    fill: 'hsl(var(--muted-foreground))',
                    fontSize: 12
                  }} dy={10} />
                    <YAxis hide domain={['dataMin - 500', 'dataMax + 500']} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                  </AreaChart>
                </ChartContainer>
              </div>
            </div>

            {}
            <div className="lg:col-span-3 border-t lg:border-t-0 lg:border-l border-border p-6 bg-primary/5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-6 flex items-center gap-2 font-default">
                <Activity className="w-4 h-4 text-secondary" /> Recent Activity
              </h3>
              <div className="space-y-4">
                {recentGains.map((gain, i) => <div key={gain.id} data-index={i} className="bg-card border border-border rounded-md p-4 hover:shadow-md transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-sm font-default text-foreground">{gain.asset}</div>
                        <div className="text-xs text-muted-foreground font-default">{gain.type} • {gain.symbol}</div>
                      </div>
                      <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary/20 font-mono">
                        {gain.profit}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center mt-3 font-default">
                      <Clock className="w-3 h-3 mr-1 opacity-70" /> {gain.time}
                    </div>
                  </div>)}
              </div>
              
              <div className="mt-8">
                <Button variant="outline" className="w-full border-border hover:bg-secondary/5 text-muted-foreground rounded-md" asChild>
                  <Link to="/LoanOptionsPage">
                    View Loan Options <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </div>

        {}
        <div className="mt-12 text-center">
          <Button size="lg" className="rounded-md px-8 bg-secondary text-secondary-foreground hover:shadow-xl transition-shadow font-default" asChild>
            <Link to="/ContactPage">
              Get Pre-Approved Now
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}